import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import type { SyntheticEvent } from 'react'

import type {
  AutocompleteOwnerState,
  AutocompleteRenderOptionState,
  AutocompleteValue,
  ChipTypeMap,
} from '@mui/material'
import {
  Autocomplete,
  autocompleteClasses,
  Popper,
  styled,
  Typography,
  unstable_composeClasses,
  useThemeProps,
} from '@mui/material'
import classnames from 'classnames'
import { isEmpty } from 'lodash-es'

import Button from '../ButtonNewest/Button'
import Chip from '../Chip/Chip'
import FeatherIcon from '../FeatherIcon/FeatherIcon'
import { IconName } from '../FeatherIcon/FeatherIcon.types'
import useAriaProps from '../Field/hooks/useAriaProps'
import useFieldSlotProps from '../Field/hooks/useFieldSlotProps'
import FieldWrapper from '../FieldWrapper/FieldWrapper'
import TextFieldBase from '../TextFieldBase/TextFieldBase'
import type { AutocompleteFieldProps, AutocompleteOption } from './AutocompleteField.types'
import autocompleteFieldClasses, { getAutocompleteFieldUtilityClass } from './autoCompleteFieldClasses'
import AutocompleteFieldListBox from './AutocompleteFieldListBox'
import AutocompleteFieldOptionBody from './AutocompleteFieldOptionBody'
import useAsyncAutocomplete from './useAsyncAutocomplete'
import VirtualizedListbox from './VirtualizedListbox'

const AutocompleteFieldRoot = styled(Autocomplete, {
  name: 'AutocompleteField',
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
})(({ theme, disabled, loading }) => ({
  '@keyframes pulse': {
    '0%, 100%': {
      backgroundColor: '#fff',
    },
    '50%': {
      backgroundColor: '#e9e9ea',
    },
  },

  [`& .${autocompleteFieldClasses.field}`]: {
    width: '100%',
  },

  [`& .${autocompleteClasses.inputRoot}`]: {
    paddingTop: 0,
    paddingBottom: 0,

    ...(disabled && {
      background: 'rgb(245, 246, 250)',
    }),

    ...(loading && {
      animation: 'pulse 2s infinite',
    }),

    [`& .${autocompleteFieldClasses.select}`]: {
      background: 'transparent',
      flex: 1,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: theme.spacing(1),
    },

    [`& .${autocompleteFieldClasses.searchIcon}`]: {
      width: 20,
      height: 20,
      '& svg': {
        stroke: 'rgba(0, 0, 0, 0.54)',
      },
    },
  },
})) as typeof Autocomplete

interface AutocompleteEndAdornmentProps {
  icon?: boolean
}

const AutocompleteEndAdornment = styled('div', {
  name: 'AutocompleteField',
  slot: 'EndAdornment',
  overridesResolver: (props, styles) => styles.endAdornment,
  shouldForwardProp: (prop) => prop !== 'icon',
})<AutocompleteEndAdornmentProps>(({ theme, icon }) => ({
  position: 'absolute',
  right: icon ? theme.spacing(1) : 0,
  top: '50%',
  transform: 'translate(0, -50%)',
  display: 'flex',
}))

const AutocompleteClearSearchButton = styled(Button, {
  name: 'AutocompleteField',
  slot: 'ClearSearchButton',
  overridesResolver: (props, styles) => styles.clearSearchButton,
})({
  marginRight: 12,
})

// Virtualized popper: the windowed listbox needs the option `<li>`s to fill the
// input width (react-window positions rows absolutely, so they don't stretch on
// their own) and box-sizing reset so heights match the fixed row sizes.
const AutocompleteVirtualizedPopper = styled(Popper, {
  name: 'AutocompleteField',
  slot: 'VirtualPopper',
  overridesResolver: (props, styles) => styles.virtualPopper,
})({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    padding: 0,
    // MUI's listbox is itself a scroller (max-height + overflow:auto). With
    // windowing, react-window's own inner viewport is the scroller — leaving
    // the outer one active creates two nested scrollers that desync, so fast
    // scrolling moves the outer container while react-window never updates its
    // window and shows blank gaps. Disable the outer scroller entirely.
    overflow: 'visible',
    maxHeight: 'none',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
})

const useUtilityClasses = <
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  ownerState: Partial<
    AutocompleteFieldProps<
      Option,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent,
      QueryVariables,
      MoreQueryVariables,
      QueryResults,
      MoreQueryResults
    >
  >,
) => {
  const slots = {
    root: ['root', ownerState.layout || 'row', ownerState.size || 'medium'],
    field: ['field'],
    label: ['label'],
    select: ['select'],
    helperText: ['helperText'],
    errorText: ['errorText'],
    list: ['list'],
    option: ['option'],
    placeholder: ['placeholder'],
    searchIcon: ['searchIcon'],
    endAdornment: ['endAdornment'],
    clearSearchButton: ['clearSearchButton'],
  }

  return unstable_composeClasses(slots, getAutocompleteFieldUtilityClass, ownerState.classes)
}

const SEARCH_MODE_FILTER_OPTIONS = <Option extends AutocompleteOption>(options: Option[]) => options

const isEmptySelection = (selection: unknown) =>
  selection === null ||
  selection === undefined ||
  selection === '' ||
  (Array.isArray(selection) && selection.length === 0)

const getSelectionId = (entry: unknown) =>
  entry && typeof entry === 'object' && 'value' in entry ? (entry as AutocompleteOption).value : entry

// Compares a display value with a `value` prop by option id, so a consumer handing back a
// fresh array — or the whole option rather than the id it was given — still counts as the
// same selection and doesn't trigger a resync.
const isSameSelection = (current: unknown, next: unknown) => {
  if (isEmptySelection(current) && isEmptySelection(next)) {
    return true
  }

  if (Array.isArray(current) || Array.isArray(next)) {
    if (!Array.isArray(current) || !Array.isArray(next) || current.length !== next.length) {
      return false
    }

    return current.every((entry, index) => getSelectionId(entry) === getSelectionId(next[index]))
  }

  return getSelectionId(current) === getSelectionId(next)
}

// Raw renderGroup so the virtualized listbox receives `[groupHeader, ...options]`
// elements and can flatten them into its own (taller) header rows. The default
// MUI renderGroup wraps options in a nested <ul>, which windowing can't measure.
const VIRTUALIZED_RENDER_GROUP = (params: { key: string; group: string; children?: React.ReactNode }) =>
  params as unknown as React.ReactNode

function _AutocompleteField<
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  inProps: AutocompleteFieldProps<
    Option,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent,
    QueryVariables,
    MoreQueryVariables,
    QueryResults,
    MoreQueryResults
  >,
) {
  const props = useThemeProps({ props: inProps, name: 'AutocompleteField' })

  const {
    className,
    name,
    label,
    error,
    helperText,
    tooltip,
    WrapperProps,
    InputLabelProps,
    FormHelperTextProps,
    ErrorTextProps,
    TextFieldProps,
    ListboxProps,
    placeholder = 'Select an item...',
    layout = 'row',
    size = 'medium',
    value,
    options: optionsProp,
    renderOption: renderOptionProp,
    asyncMode = 'static',
    loadData,
    loadMore,
    getOptions,
    getQueryVariables,
    getPageCursor,
    loadChildData,
    optionHasChild,
    getChildOptions,
    getChildQueryVariables,
    onChange,
    onBlur,
    onInputChange,
    isOptionEqualToValue: isOptionEqualToValueProp,
    searchDebounceTimeout,
    minimumSearchLength,
    checkbox,
    multiControls,
    clearText = 'Clear',
    responsive,
    searchIconPosition = 'end',
    optionClassName,
    virtualize = false,
    ...rootProps
  } = props

  if (!loadData && !optionsProp) {
    console.error('You must provide either `options` or `loadData` prop')
  }

  if (loadData && optionsProp) {
    console.warn(
      'Providing both `options` & `loadData` props will result in `options` being ignored. Is this what you want?',
    )
  }

  const slotClasses = useUtilityClasses({ ...props, layout, size, disabled: props.disabled })

  const { ariaId, labelId, fieldAriaProps } = useAriaProps(props)

  const { labelProps, helperTextProps, errorProps } = useFieldSlotProps(slotClasses, {
    ...props,
    FormHelperTextProps,
    ErrorTextProps,
    InputLabelProps,
  })

  const isAsync = Boolean(loadData)
  const isSearchMode = asyncMode === 'search'

  const asyncOptions = useAsyncAutocomplete<Option, QueryVariables, MoreQueryVariables, QueryResults, MoreQueryResults>(
    {
      defaultValue: value as AutocompleteValue<Option, boolean, boolean, boolean>,
      skip: !isAsync,
      mode: asyncMode,
      loadData: loadData!,
      loadMore,
      loadChildData,
      optionHasChild,
      getChildOptions,
      getChildQueryVariables,
      getOptions,
      getQueryVariables,
      getPageCursor,
      onChange: onChange as AutocompleteFieldProps<Option, boolean, boolean, boolean>['onChange'],
      onInputChange,
      searchThrottleTimeout: searchDebounceTimeout,
      minimumSearchLength,
      multiple: Boolean(props.multiple),
    },
  )

  const { fieldProps: asyncFieldProps, listboxProps: asyncListboxProps } = asyncOptions || {}

  const { onClearAll: asyncOnClearAll, ...listboxProps } = asyncListboxProps || {}

  const options = useMemo(() => {
    if (loadData) {
      return asyncFieldProps?.options
    }

    if (!optionsProp) {
      return []
    }

    return getOptions?.(optionsProp) ?? optionsProp
  }, [asyncFieldProps?.options, getOptions, loadData, optionsProp])

  const optionFromValue = useMemo<AutocompleteValue<Option, boolean, DisableClearable, FreeSolo>>(() => {
    if (isSearchMode) {
      return null as AutocompleteValue<Option, boolean, DisableClearable, FreeSolo>
    }

    if (props.multiple) {
      return Array.isArray(value)
        ? value.reduce(
            (acc, val) => {
              const option = options?.find((option) => option.value === val)

              if (option) {
                acc.push(option)
              }

              return acc
            },
            [] as AutocompleteValue<Option, true, DisableClearable, FreeSolo>,
          )
        : ([] as AutocompleteValue<Option, true, DisableClearable, FreeSolo>)
    }

    return (
      options?.length && (typeof value === 'string' || typeof value === 'number')
        ? options?.find((option) => option.value === value)
        : value
    ) as AutocompleteValue<Option, false, DisableClearable, FreeSolo>
  }, [isSearchMode, options, props.multiple, value])

  const resetValue = useRef<AutocompleteValue<Option, boolean, DisableClearable, FreeSolo>>(props.multiple ? [] : null)

  // Search mode has no option list to derive the display from (`optionFromValue` is forced
  // to null), so the selection is mirrored here. The mirror also keeps the component
  // interactive when no `value` prop is given.
  const [selectedValue, setSelectedValue] = React.useState<AutocompleteValue<
    Option,
    boolean,
    DisableClearable,
    FreeSolo
  > | null>(() =>
    isSearchMode && !isEmptySelection(value)
      ? (value as AutocompleteValue<Option, boolean, DisableClearable, FreeSolo>)
      : resetValue.current,
  )

  // In search mode the mirror follows `value` on every change, so an external update
  // reaches the display and a change the consumer declines to commit reverts.
  useEffect(() => {
    if (!isSearchMode || value === undefined) {
      return
    }

    setSelectedValue((current) =>
      isSameSelection(current, value)
        ? current
        : ((isEmptySelection(value) ? resetValue.current : value) as AutocompleteValue<
            Option,
            boolean,
            DisableClearable,
            FreeSolo
          >),
    )
  }, [isSearchMode, value])

  // The `value` prop is the display source of truth whenever it is provided, so an external
  // change always reaches the control and a declined change reverts on the next render.
  const fieldValue =
    value !== undefined && !isSearchMode
      ? isEmptySelection(optionFromValue)
        ? resetValue.current
        : optionFromValue
      : selectedValue

  const [inputValue, setInputValue] = React.useState('')

  const handleInputChange = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo>>['onInputChange']
  >(
    (event, newInputValue, reason) => {
      setInputValue(newInputValue)
      onInputChange?.(event, newInputValue, reason)
    },
    [onInputChange],
  )

  const getOptionLabel = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo>>['getOptionLabel']
  >(
    (value) => {
      if (value && rootProps.getOptionLabel) {
        return rootProps.getOptionLabel(value)
      }

      const options = asyncFieldProps?.options || optionsProp

      const label =
        typeof value === 'number' || typeof value === 'string'
          ? options?.find((option) => option.value === value)?.label
          : value.label

      return label ?? ''
    },
    [asyncFieldProps?.options, optionsProp, rootProps],
  )

  const isOptionEqualToValue = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo>>['isOptionEqualToValue']
  >(
    (option, value) => {
      if (isOptionEqualToValueProp) {
        return isOptionEqualToValueProp(option, value)
      }

      if (typeof value === 'object' && 'value' in value) {
        return option.value === value.value
      }

      return option.value === value
    },
    [isOptionEqualToValueProp],
  )

  const renderOption = useMemo(() => {
    const defaultRenderOption: Required<
      AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>
    >['renderOption'] = (props2: { key?: string } & React.HTMLAttributes<HTMLLIElement>, option, { selected }) => {
      // Strip MUI's default option key (it defaults to the option label, which
      // collides when two options share a label, e.g. two providers named the
      // same) and key by the unique option value instead.
      const { key: _key, ...otherProps } = props2
      const key = option.value

      // Shared option body — identical markup in the windowed and non-windowed
      // paths. Only the wrapper differs: a real `<li>` here, a tuple below.
      const body = (
        <AutocompleteFieldOptionBody
          name={name}
          optionValue={option.value}
          checkbox={checkbox}
          selected={selected}
          label={getOptionLabel?.(option) ?? option.label}
        />
      )

      if (virtualize) {
        // MUI's virtualization recipe: hand back the raw tuple. The windowed row
        // renderer wraps it in the absolutely-positioned `<li>` and renders the
        // shared body from `props.children`.
        return [
          {
            ...otherProps,
            key,
            className: classnames(optionClassName, otherProps.className, slotClasses.option),
            children: body,
          },
          option,
          { selected },
        ] as unknown as React.ReactNode
      }

      return (
        <li key={key} {...otherProps} className={classnames(optionClassName, otherProps.className, slotClasses.option)}>
          {body}
        </li>
      )
    }

    return renderOptionProp
      ? (
          props: { key: string } & React.HTMLAttributes<HTMLLIElement>,
          option: Option,
          state: AutocompleteRenderOptionState,
          ownerState: AutocompleteOwnerState<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>,
        ) =>
          renderOptionProp(
            { ...props, className: classnames(optionClassName, slotClasses.option) },
            option,
            state,
            ownerState,
          )
      : defaultRenderOption
  }, [renderOptionProp, optionClassName, slotClasses.option, checkbox, name, getOptionLabel, virtualize])

  const asyncOnChange = asyncFieldProps?.onChange

  const handleChange = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>>['onChange']
  >(
    (...args) => {
      setSelectedValue(args[1])

      if (asyncOnChange) {
        asyncOnChange(...args)
      } else if (onChange) {
        onChange(...args)
      }
    },
    [asyncOnChange, onChange],
  )

  const handleSelectAll = useCallback(() => {
    if (options?.length) {
      setSelectedValue(options)
      // Propagate to the form the same way handleClearAll does — without this the
      // select-all only updates the visual selection, never RHF/FF state, so
      // validation and the submitted payload miss the selection.
      onChange?.(
        {} as SyntheticEvent,
        options as AutocompleteValue<Option, Multiple, DisableClearable, FreeSolo>,
        'selectOption',
      )
    }
  }, [options, onChange])

  const handleClearAll = useCallback(() => {
    setSelectedValue(resetValue.current)

    if (asyncOnClearAll) {
      asyncOnClearAll?.()
    } else {
      setInputValue('')
    }

    onChange?.(
      {} as SyntheticEvent,
      resetValue.current as AutocompleteValue<Option, Multiple, DisableClearable, FreeSolo>,
      'clear',
    )
  }, [asyncOnClearAll, onChange])

  const handleClearInput = useCallback(() => {
    setSelectedValue(resetValue.current)

    if (asyncOnClearAll) {
      asyncOnClearAll?.()
    } else {
      setInputValue('')
    }

    if (asyncMode === 'search' && !props.multiple) {
      asyncFieldProps?.onClose()
    }

    onChange?.(
      {} as SyntheticEvent,
      resetValue.current as AutocompleteValue<Option, Multiple, DisableClearable, FreeSolo>,
      'clear',
    )
  }, [asyncFieldProps, asyncMode, asyncOnClearAll, onChange, props.multiple])

  const disabled = props.disabled || (asyncFieldProps?.loading && !asyncFieldProps?.open)

  const searchEndAdornment = (() => {
    if (!props.disableClearable && (asyncFieldProps?.inputValue || fieldValue)) {
      return (
        <AutocompleteEndAdornment className={slotClasses.endAdornment}>
          <AutocompleteClearSearchButton
            variant="text"
            color="secondary"
            onClick={handleClearInput}
            className={slotClasses.clearSearchButton}
            disabled={rootProps.loading || asyncFieldProps?.loading}
          >
            {clearText}
          </AutocompleteClearSearchButton>
        </AutocompleteEndAdornment>
      )
    }

    if (searchIconPosition === 'end') {
      return (
        <AutocompleteEndAdornment className={slotClasses.endAdornment} icon>
          <FeatherIcon className={slotClasses.searchIcon} name={IconName.Search} />
        </AutocompleteEndAdornment>
      )
    }

    return null
  })()

  const renderTags = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>>['renderTags']
  >(
    (tagValue, getTagProps) => {
      const tagLimit = props.limitTags || 3

      if (tagValue.length > tagLimit) {
        return <Typography>{`${tagValue.length} Selected`}</Typography>
      } else {
        return tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index })

          return (
            <Chip key={key} label={option.label} {...tagProps} size={props.size === 'large' ? 'medium' : 'small'} />
          )
        })
      }
    },
    [props.limitTags, props.size],
  )

  const handleBlur = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>>['onBlur']
  >(
    (event) => {
      if (fieldValue && !value && props.clearOnBlur) {
        setSelectedValue(resetValue.current)
      }

      if (onBlur) {
        onBlur(event)
      }
    },
    [fieldValue, onBlur, props.clearOnBlur, value],
  )

  return (
    <FieldWrapper
      {...WrapperProps}
      name={name}
      label={label}
      tooltip={tooltip}
      layout={layout}
      responsive={responsive}
      error={error}
      helperText={helperText}
      size={size}
      // The field's own `disabled`, not the derived one: an async load also disables the input,
      // but shouldn't grey the label.
      disabled={props.disabled}
      className={classnames(slotClasses.root, WrapperProps?.className)}
      InputLabelProps={{ ...InputLabelProps, ...labelProps, id: labelId }}
      FormHelperTextProps={{ ...FormHelperTextProps, ...helperTextProps }}
      ErrorTextProps={{ ...ErrorTextProps, ...errorProps }}
    >
      <AutocompleteFieldRoot
        {...rootProps}
        // @ts-expect-error - MUI types are incorrect for custom components
        ListboxComponent={virtualize ? VirtualizedListbox : AutocompleteFieldListBox}
        ListboxProps={{
          ...ListboxProps,
          className: classnames(ListboxProps?.className, slotClasses.list),
          multiControls,
          // checkbox is only consumed by VirtualizedListbox (row sizing); gating it
          // behind virtualize keeps it off the non-virtualized <ul> DOM node.
          ...(virtualize && { checkbox }),
          // @ts-expect-error - MUI's ListboxProps type only allows <ul> attributes;
          // our custom listbox also consumes selectedCount, optionCount, onClearAll
          // and onSelectAll (plus multiControls/checkbox above), which MUI's types
          // don't model.
          selectedCount: Array.isArray(fieldValue) ? fieldValue?.length : undefined,
          optionCount: options?.length,
          onClearAll: handleClearAll,
          onSelectAll: handleSelectAll,
          ...listboxProps,
        }}
        {...(virtualize && {
          disableListWrap: true,
          PopperComponent: AutocompleteVirtualizedPopper,
          renderGroup: VIRTUALIZED_RENDER_GROUP,
        })}
        value={fieldValue as AutocompleteValue<Option, Multiple, DisableClearable, FreeSolo>}
        renderTags={rootProps.renderTags ?? renderTags}
        renderOption={renderOption}
        id={ariaId}
        autoComplete
        disableCloseOnSelect={!!props.multiple || !!props.disableCloseOnSelect}
        isOptionEqualToValue={isOptionEqualToValue}
        clearText={clearText}
        renderInput={(params) => (
          <TextFieldBase
            {...TextFieldProps}
            {...params.InputProps}
            // MUI hands `disabled` to renderInput outside InputProps; without it the input root
            // never gets the disabled class, so it keeps the enabled background and text colour.
            disabled={params.disabled}
            name={name}
            inputRef={TextFieldProps?.inputRef || params.InputProps.ref}
            placeholder={isEmpty(fieldValue) ? placeholder : ''}
            inputProps={{
              ...params.inputProps,
              ...fieldAriaProps,
              ...TextFieldProps?.inputProps,
              className: classnames(
                TextFieldProps?.inputProps?.className,
                params.inputProps.className,
                slotClasses.select,
              ),
            }}
            className={classnames(
              TextFieldProps?.className,
              slotClasses.field,
              params.InputProps.className,
              'TextField-field',
            )}
            startAdornment={
              asyncMode === 'search' && searchIconPosition === 'start' ? (
                <>
                  <FeatherIcon className={slotClasses.searchIcon} name={IconName.Search} />
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              )
            }
            endAdornment={asyncMode === 'search' ? searchEndAdornment : params.InputProps.endAdornment}
          />
        )}
        {...asyncFieldProps}
        loading={rootProps.loading || asyncFieldProps?.loading}
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        options={options || []}
        onChange={handleChange}
        onBlur={handleBlur}
        className={classnames(slotClasses.field, className)}
        inputValue={isAsync ? asyncFieldProps?.inputValue : inputValue}
        onInputChange={
          asyncFieldProps?.onInputChange !== undefined ? asyncFieldProps?.onInputChange : handleInputChange
        }
        filterOptions={asyncMode === 'search' ? SEARCH_MODE_FILTER_OPTIONS : props.filterOptions}
      />
    </FieldWrapper>
  )
}

const AutocompleteField = React.memo(_AutocompleteField) as typeof _AutocompleteField

export default AutocompleteField
